/**
 * EligibilityPanel — Phase 3 traffic light.
 *
 * Calls GET /api/v2/rfp/{rfpId}/eligibility (real endpoint, no
 * placeholder) and renders one row per extracted requirement with
 * green / amber / red / grey status (PRD §7.2 AC-E-1).
 *
 * Drill-down: clicking a row expands the matching ProductCertification
 * IDs we hold so the user can verify which cert backs the verdict.
 *
 * Override: the API tolerates a missing or partial analysis and returns
 * an empty rows[] — in that case we show a hint to re-run analysis,
 * not a scary error.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, RefreshCw,
  ChevronDown, ChevronRight, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { v2 } from "@/lib/v2Api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";


const STATUS_META = {
  green:   { label: "Held & valid",       icon: ShieldCheck,    cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  amber:   { label: "Held, attention",    icon: ShieldAlert,    cls: "text-amber-700  bg-amber-50  border-amber-200"  },
  red:     { label: "Missing or expired", icon: ShieldX,        cls: "text-rose-700   bg-rose-50   border-rose-200"   },
  unknown: { label: "Needs review",       icon: ShieldQuestion, cls: "text-slate-600  bg-slate-50  border-slate-200"  },
};


function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const Icon = meta.icon;
  return (
    <Badge className={`border inline-flex items-center gap-1 ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </Badge>
  );
}


function EligibilityRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const showExpand = row.matched_cert_ids?.length > 0 || row.note;
  return (
    <Card>
      <CardContent className="py-3">
        <button
          onClick={() => showExpand && setExpanded(!expanded)}
          className={`w-full text-left flex items-start justify-between gap-3 ${
            showExpand ? "cursor-pointer" : "cursor-default"
          }`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <StatusBadge status={row.status} />
              {/* Tier 1: criticality chip — mandatory rows pop in
                  rose so a missed obligation reads as urgent before
                  the user expands the row. Desirable + unspecified
                  stay neutral. */}
              {row.criticality === "mandatory" && (
                <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[10px]">
                  Mandatory
                </Badge>
              )}
              {row.criticality === "desirable" && (
                <Badge variant="outline" className="text-[10px] text-slate-600">
                  Desirable
                </Badge>
              )}
              {row.matched_kind && (
                <Badge variant="outline" className="text-[10px] font-mono">
                  {row.matched_kind}
                </Badge>
              )}
              {row.days_to_expiry != null && row.days_to_expiry >= 0 && row.days_to_expiry <= 90 && (
                <Badge variant="outline" className="text-[10px] text-amber-700">
                  expires in {row.days_to_expiry}d
                </Badge>
              )}
              {row.days_to_expiry != null && row.days_to_expiry < 0 && (
                <Badge variant="outline" className="text-[10px] text-rose-700">
                  expired {Math.abs(row.days_to_expiry)}d ago
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-700">{row.requirement_text}</p>
            {row.note && (
              <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {row.note}
              </p>
            )}
          </div>
          {showExpand && (
            <span className="text-slate-400 shrink-0">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          )}
        </button>
        {expanded && row.matched_cert_ids?.length > 0 && (
          <div className="mt-2 pl-4 border-l-2 border-slate-200 text-xs text-slate-500">
            <p className="uppercase tracking-wider font-medium text-slate-400 mb-1">
              Matched certificates ({row.matched_cert_ids.length})
            </p>
            <ul className="space-y-0.5 font-mono">
              {row.matched_cert_ids.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
            {row.valid_until && (
              <p className="mt-1 text-slate-600 font-sans">
                Nearest expiry: <strong>{row.valid_until}</strong>
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ScoreRing({ score, summary }) {
  // ``score === null`` means the backend extracted ZERO requirements
  // from the tender. We deliberately render "N/A" instead of a
  // misleading numeric score — a "100" used to lie to BD that they
  // were ready to bid when actually the parser found nothing to
  // verify against. Honest empty state > false confidence.
  const isUnknown = score == null;
  const colour = isUnknown
    ? "text-slate-500"
    : score >= 90 ? "text-emerald-600"
    : score >= 60 ? "text-amber-600"
    :               "text-rose-600";
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-semibold tabular-nums ${colour}`}>
            {isUnknown ? "N/A" : score.toFixed(0)}
          </div>
          <div className="text-xs text-slate-500">
            <p className="uppercase tracking-wider font-medium">Eligibility score</p>
            <p className="mt-0.5">
              {isUnknown
                ? "No certification requirements extracted yet — re-analyse the tender or add manually."
                : <>
                    {summary.green} valid · {summary.amber} attention · {summary.red} missing
                    {summary.unknown > 0 ? ` · ${summary.unknown} unclear` : ""}
                  </>}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


export default function EligibilityPanel({ rfpId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!rfpId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await v2.get(`/rfp/${rfpId}/eligibility`);
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail ?? err.message ?? "Failed to load eligibility";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [rfpId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!rfpId) {
    return <p className="p-4 text-sm text-slate-500">No tender selected.</p>;
  }
  if (loading && !data) {
    return <p className="p-4 text-sm text-slate-500">Computing eligibility…</p>;
  }
  if (error && !data) {
    return (
      <div className="p-4 space-y-2">
        <p className="text-sm text-rose-700">{error}</p>
        <Button size="sm" variant="outline" onClick={refresh}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
        </Button>
      </div>
    );
  }
  if (!data) return null;

  const noRows = (data.rows ?? []).length === 0;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Eligibility</h2>
          <p className="text-xs text-slate-500">
            Each requirement extracted from the tender, joined against our
            <code className="text-[10px] bg-slate-100 px-1 mx-1 rounded">ProductCertification</code>
            roster. Score feeds the Bid Priority sub-weighting (PRD §7.2).
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={refresh}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      <ScoreRing score={data.score} summary={data.summary} />

      {/* Tier 1: top-of-panel banner when ANY mandatory cert is
          unverified (red / amber / unknown). Sits ABOVE the row list
          so a BD user can't miss it while skimming. */}
      {(data.summary?.mandatory_unverified ?? 0) > 0 && (
        <Card className="border-rose-300 bg-rose-50/40">
          <CardContent className="py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5 shrink-0" />
            <div className="text-sm text-rose-900">
              <p className="font-semibold">
                {data.summary.mandatory_unverified} mandatory{" "}
                {data.summary.mandatory_unverified === 1 ? "requirement is" : "requirements are"}{" "}
                unverified.
              </p>
              <p className="text-xs text-rose-800 mt-0.5">
                These are flagged "shall / must / mandatory" in the tender.
                Missing or expiring certs here are dis-qualification risk.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {noRows ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500 text-sm">
            No certification requirements were extracted from this tender yet.
            Re-run analysis from the Brief tab, or add them manually under
            <strong> Settings → Fine tuning</strong>.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.rows.map((row, i) => (
            <EligibilityRow key={`${row.matched_kind || "unknown"}-${i}`} row={row} />
          ))}
        </div>
      )}

      <AllPqcRequirements rfpId={rfpId} />
    </div>
  );
}


/**
 * AllPqcRequirements — secondary panel under the certification roster.
 *
 * Reads ALL eligibility / certification fields the extractor pulled
 * from the tender (analysis.fields where kind ∈ {eligibility,
 * certification}), grouped by their value.kind sub-category
 * (experience / turnover / certification / etc), with page citation.
 *
 * This is the "70 PQC items" view the user asked for — distinct from
 * the certification-roster traffic light above, which only covers the
 * fixed cert types we track (NABL, ISO 9001, …).
 */
function AllPqcRequirements({ rfpId }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!rfpId) return;
    setLoading(true);
    setError(null);
    v2.get(`/rfp/${rfpId}/analysis`)
      .then((r) => {
        const fields = r.data?.analysis?.fields || {};
        const rows = [];
        Object.values(fields).forEach((arr) => {
          (arr || []).forEach((f) => {
            if (f.kind !== "eligibility" && f.kind !== "certification") return;
            const v = f.value || {};
            const text = (v.criterion || "").trim();
            if (!text) return;
            rows.push({
              criterion: text,
              sub_kind: (v.kind || "other").toLowerCase(),
              threshold_inr: v.threshold_inr ?? null,
              confidence: f.confidence ?? 0,
              page: f.citations?.[0]?.page ?? null,
            });
          });
        });
        // Dedupe by leading 120 chars (PQC clauses often repeat across
        // the checklist + format annex).
        const seen = new Set();
        const deduped = [];
        for (const r of rows) {
          const k = r.criterion.slice(0, 120).toLowerCase();
          if (seen.has(k)) continue;
          seen.add(k);
          deduped.push(r);
        }
        setItems(deduped);
      })
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "failed"))
      .finally(() => setLoading(false));
  }, [rfpId]);

  if (!rfpId) return null;

  const grouped = (items || []).reduce((acc, it) => {
    const k = it.sub_kind || "other";
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});
  const groupLabel = {
    experience: "Experience",
    turnover: "Turnover / Financials",
    certification: "Certifications",
    networth: "Net Worth",
    license: "Licenses & Registration",
    blacklist: "Blacklisting / Standing",
    other: "Other",
  };
  const order = ["experience", "turnover", "networth", "certification", "license", "blacklist", "other"];

  return (
    <Card className="border-slate-200 mt-2">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <CardTitle className="text-base flex items-center justify-between">
          <span>
            All PQC Requirements from this Tender
            {items != null && (
              <Badge variant="outline" className="ml-2">{items.length}</Badge>
            )}
          </span>
          <span className="text-xs text-slate-500">{expanded ? "▾ hide" : "▸ show"}</span>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Every requirement the extractor pulled from the tender body — including
          ones beyond the certification roster above. Use the Bid Matrix tab to
          see which we satisfy.
        </p>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-slate-500">Loading requirements…</p>}
          {error && <p className="text-sm text-rose-700">{error}</p>}
          {items != null && items.length === 0 && (
            <p className="text-sm text-slate-500 italic">
              No PQC requirements detected. Re-analyse may help if this tender was
              processed before the extractor improvements.
            </p>
          )}
          {order.map((k) =>
            grouped[k] ? (
              <div key={k}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
                  {groupLabel[k] || k}  ·  {grouped[k].length}
                </p>
                <ul className="space-y-1.5">
                  {grouped[k].map((it, i) => (
                    <li
                      key={`${k}-${i}`}
                      className="text-sm text-slate-800 border border-slate-100 rounded px-3 py-2 bg-slate-50/60"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="flex-1">{it.criterion}</span>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {it.threshold_inr != null && (
                            <span className="mr-2 text-slate-600">
                              ₹{Number(it.threshold_inr).toLocaleString()}
                            </span>
                          )}
                          {it.page != null && <>p.{it.page}</>}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          )}
        </CardContent>
      )}
    </Card>
  );
}
